import { DialogComponent } from '@theme/dialog';

/**
 * A dialog that plays a video next to a list of the products featured in it.
 *
 * The video markup lives inside a `<template>` so nothing is downloaded until the dialog is
 * opened, and it is removed again on close. Removing the element is what stops playback for
 * both uploaded videos and YouTube/Vimeo iframes.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLTemplateElement} [videoTemplate] - Holds the video markup until first open.
 * @property {HTMLElement} [videoContainer] - Where the video markup is mounted.
 *
 * @extends DialogComponent
 */
class ShoppableVideoComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('dialog:open', this.#mountVideo);
    this.addEventListener('dialog:close', this.#unmountVideo);
  }

  disconnectedCallback() {
    this.removeEventListener('dialog:open', this.#mountVideo);
    this.removeEventListener('dialog:close', this.#unmountVideo);

    super.disconnectedCallback();
  }

  #mountVideo = () => {
    const { videoTemplate, videoContainer } = this.refs;

    if (!(videoTemplate instanceof HTMLTemplateElement)) return;
    if (!(videoContainer instanceof HTMLElement) || videoContainer.firstElementChild) return;

    videoContainer.appendChild(videoTemplate.content.cloneNode(true));
  };

  #unmountVideo = () => {
    const { videoContainer } = this.refs;

    if (!(videoContainer instanceof HTMLElement)) return;

    videoContainer.replaceChildren();
  };
}

if (!customElements.get('shoppable-video-component')) {
  customElements.define('shoppable-video-component', ShoppableVideoComponent);
}
